import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { CAP_TABLE_TEMPLATE_LINK } from "constants/capTable";
import { openLink } from "utils/openLink";
import { EmptyStateWindIcon } from "components/icons/EmptyStateWindIcon";
import { TableListIcon } from "components/icons/TableListIcon";
import { useGetAllCapTablesQuery } from "store/apis/capTable";
import { CapTable } from "./CapTable";
import { CAP_TABLE_ACCEPTED_FILES, UploadCapTable } from "./UploadCapTable";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";

const EmptyCapTable = ({
  open,
  onFileDrop,
}: {
  open: () => void;
  onFileDrop: (file: File) => void;
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: CAP_TABLE_ACCEPTED_FILES,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        onFileDrop(acceptedFiles[0]);
      }
    },
  });

  return (
    <div
      {...getRootProps()}
      className={classNames(
        "t-flex t-w-full t-flex-col t-items-center t-justify-center t-gap-4 t-rounded-md t-border t-border-dashed t-bg-surface-lighter-grey t-py-9 sm:t-px-4",
        isDragActive
          ? "t-border-purple-30 t-bg-surface-purple"
          : "t-border-neutral-20"
      )}
    >
      <input {...getInputProps()} />
      <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-3">
        <EmptyStateWindIcon />
        <div className="t-flex t-flex-col t-items-center t-gap-1 t-text-center">
          <div className="t-text-subtext-sm t-text-text-60">
            Drop your cap table here
          </div>
          <div className="t-text-body-sm t-text-neutral-50">
            We don&apos;t have a cap table for your company yet.
            <br />
            Drag and drop it here, or choose a file to upload.
          </div>
        </div>
      </div>
      <div className="t-flex t-items-start t-justify-center t-gap-2 sm:t-flex-wrap">
        <Button customType="primary" onClick={open} size="small">
          Upload cap table
        </Button>
        <Button
          customType="secondary"
          onClick={() => openLink(CAP_TABLE_TEMPLATE_LINK)}
          size="small"
        >
          Download template
        </Button>
      </div>
    </div>
  );
};

export const CapTableHome = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { entityId } = useParams<{ entityId: string }>();
  const { isOpen, open, close } = useModal();
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  const { data, isLoading, isSuccess } = useGetAllCapTablesQuery({
    pageNum: 1,
    groupId,
    entityId,
  });

  const { cap_tables: capTableData = [] } = data || {};

  const isEmpty = capTableData.length === 0;

  const handleFileDrop = (file: File) => {
    setDroppedFile(file);
    open();
  };

  const handleUploadClose = () => {
    close();
    setDroppedFile(null);
  };

  return (
    <div className="t-flex t-flex-col t-rounded-xl t-border t-border-solid t-border-neutral-10 t-bg-surface t-p-5">
      <div className="t-mb-5 t-flex t-items-start t-justify-between">
        <div className="t-flex t-items-start t-gap-3">
          <span className="t-flex t-h-9 t-w-9 t-flex-shrink-0 t-items-center t-justify-center t-rounded t-bg-neutral-0 t-text-neutral-40">
            <TableListIcon />
          </span>
          <div>
            <div className="t-text-subtext t-text-text-100">Captable</div>
            <div className="t-text-body-sm t-text-text-30">
              Individuals and entities that own shares in the company.
            </div>
          </div>
        </div>
        {!isEmpty && (
          <Button customType="primary" onClick={open} size="small">
            Upload
          </Button>
        )}
      </div>

      <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
        <Async.Empty>
          <EmptyCapTable open={open} onFileDrop={handleFileDrop} />
        </Async.Empty>
        <Async.Success>
          <CapTable />
        </Async.Success>
      </Async.Root>
      <UploadCapTable
        isOpen={isOpen}
        close={handleUploadClose}
        initialFile={droppedFile}
      />
    </div>
  );
};
